import React from "react";
import { MessageCircle, Smartphone, ShoppingCart, Gift, Users, CheckCircle } from "lucide-react";
import config from './config';

const MobileRedirectPage = ({
  referralUrl,
  sendReferral,
  statusMessage,
  QRCodeGenerator,
}) => {
  var AppLogo = config.getProviderSetting().fsLogo;

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />

      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── DESIGN TOKENS ──────────────────────────────────
           Primary:   #ff1f00  (brand red)
           Accent:    #6B95C7  (brand blue)
           Dark bg:   #0f1117  (near-black)
           Surface:   #1a1d27  (raised dark)
           Border:    rgba(255,255,255,0.08)
           Text-1:    #ffffff
           Text-2:    rgba(255,255,255,0.55)
           Text-3:    rgba(255,255,255,0.28)
        ─────────────────────────────────────────────────── */

        body {
          font-family: 'Poppins', sans-serif !important;
          background: #0f1117;
          color: #ffffff;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
        }

        .mc-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 15px;
        }

        /* ═══════════════════════════════════════
           NAVBAR
        ═══════════════════════════════════════ */
        .mc-nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          height: 68px;
          background: rgba(15,17,23,0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
            padding-top: 15px;
        }

        .mc-nav-inner {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .mc-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
        }

        .mc-brand-logo {
          width: 38px; height: 38px;
          border-radius: 10px;
          overflow: hidden;
          background: #ff1f00;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
          box-shadow: 0 0 20px rgba(255,31,0,0.35);
        }
        .mc-brand-logo img { width: 100%; height: 100%; object-fit: cover; }

        .mc-brand-name {
          font-size: 1.1rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.02em;
          line-height: 1.15;
        }
        .mc-brand-tagline {
          font-size: 0.62rem;
          color: rgba(255,255,255,0.35);
          font-weight: 500;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .mc-nav-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,31,0,0.1);
          border: 1px solid rgba(255,31,0,0.2);
          border-radius: 40px;
          padding: 5px 14px;
          font-size: 0.68rem;
          font-weight: 600;
          color: #ff6b52;
          letter-spacing: 0.04em;
        }

        /* ═══════════════════════════════════════
           HERO
        ═══════════════════════════════════════ */
        .mc-hero {
          min-height: 100vh;
          padding-top: 68px;
          display: flex;
          align-items: center;
          position: relative;
          overflow: hidden;
          background: #0f1117;
        }

        /* gradient mesh background */
        .mc-hero-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .mc-hero-bg::before {
          content: '';
          position: absolute;
          width: 800px; height: 800px;
          background: radial-gradient(circle, rgba(255,31,0,0.1) 0%, transparent 65%);
          top: -300px; left: -200px;
          animation: mc-pulse 8s ease-in-out infinite alternate;
        }
        .mc-hero-bg::after {
          content: '';
          position: absolute;
          width: 700px; height: 700px;
          background: radial-gradient(circle, rgba(107,149,199,0.12) 0%, transparent 65%);
          bottom: -200px; right: -100px;
          animation: mc-pulse 10s ease-in-out infinite alternate-reverse;
        }

        @keyframes mc-pulse {
          from { transform: scale(1) translate(0, 0); }
          to   { transform: scale(1.1) translate(20px, 15px); }
        }

        .mc-hero-grid {
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: 1fr 400px;
          align-items: center;
          gap: 72px;
          padding: 64px 0 80px;
        }

        /* eyebrow tag */
        .mc-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255,31,0,0.1);
          border: 1px solid rgba(255,31,0,0.22);
          border-radius: 40px;
          padding: 6px 14px;
          font-size: 0.67rem;
          font-weight: 600;
          color: #ff8570;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 22px;
        }

        .mc-hero-title {
          font-size: clamp(2.6rem, 4vw, 3.5rem);
          line-height: 1.18;
          color: #ffffff;
          margin-bottom: 20px;
        }
        .mc-hero-title .hi { color: #ff1f00; }

        .mc-hero-sub {
          font-size: 1.05rem;
          color: rgba(255,255,255,0.5);
          line-height: 1.75;
          margin-bottom: 32px;
          max-width: 460px;
          font-weight: 400;
        }

        /* chips */
        .mc-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 50px;
        }

        .mc-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 0.74rem;
          font-weight: 600;
          color: rgba(255,255,255,0.7);
          transition: border-color 0.2s, background 0.2s;
        }
        .mc-chip:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.18);
        }
        .mc-chip svg { color: #ff1f00; flex-shrink: 0; }

        /* stats row */
        .mc-stats-strip {
          display: flex;
          gap: 50px;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .mc-snum {
          font-size: 1.6rem;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
          margin-bottom: 3px;
          letter-spacing: -0.03em;
        }
        .mc-snum em { font-style: normal; color: #ff1f00; }
        .mc-slbl {
          font-size: 0.7rem;
          color: rgba(255,255,255,0.35);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        /* ── SCANNER CARD ── */
        .mc-card {
          background: #1a1d27;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 36px 30px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          overflow: hidden;
          box-shadow: 0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04);
        }

        /* glow accent on top */
        .mc-card::before {
          content: '';
          position: absolute;
          top: 0; left: 20%; right: 20%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,31,0,0.6), rgba(107,149,199,0.6), transparent);
        }

        /* subtle inner glow */
        .mc-card::after {
          content: '';
          position: absolute;
          top: -80px; left: 50%;
          transform: translateX(-50%);
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(255,31,0,0.08) 0%, transparent 70%);
          pointer-events: none;
        }

        .mc-card-label {
          font-size: 0.66rem;
          font-weight: 700;
          color: rgba(255,255,255,0.3);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 22px;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
          position: relative;
          z-index: 1;
        }

        .mc-qr-wrap {
          background: #ffffff;
          border-radius: 16px;
          padding: 14px;
          margin-bottom: 14px;
          position: relative;
          z-index: 1;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
        }

        .mc-qr-hint {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.3);
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          gap: 5px;
          position: relative;
          z-index: 1;
        }

        .mc-or {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          margin-bottom: 16px;
          font-size: 0.64rem;
          color: rgba(255,255,255,0.18);
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          position: relative;
          z-index: 1;
        }
        .mc-or::before, .mc-or::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.07);
        }

        .mc-wa-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #25D366;
          color: #fff;
          padding: 13px 20px;
          border-radius: 12px;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          border: none;
          cursor: pointer;
          width: 100%;
          margin-bottom: 12px;
          box-shadow: 0 4px 20px rgba(37,211,102,0.3);
          transition: transform 0.15s, box-shadow 0.15s;
          position: relative;
          z-index: 1;
        }
        .mc-wa-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(37,211,102,0.42);
        }

        .mc-status {
          width: 100%;
          padding: 9px 12px;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 500;
          text-align: center;
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
        }
        .mc-status.success { background: #dcfce7; color: #15803d; }
        .mc-status.error   { background: #fee2e2; color: #b91c1c; }
        .mc-status.info    { background: #dbeafe; color: #1d4ed8; }

        .mc-card-note {
          font-size: 0.7rem;
          color: rgba(255,255,255,0.22);
          text-align: center;
          line-height: 1.5;
          position: relative;
          z-index: 1;
        }
        .mc-card-note strong { color: rgba(255,255,255,0.5); }

        /* ═══════════════════════════════════════
           HOW IT WORKS
           - white bg, fully light, clean cards
        ═══════════════════════════════════════ */
        .mc-hiw {
          background: #ffffff;
          padding: 100px 0;
        }

        .mc-hiw-head {
          text-align: center;
          max-width: 540px;
          margin: 0 auto 72px;
        }

        .mc-pill {
          display: inline-block;
          background: rgba(255,31,0,0.07);
          border: 1px solid rgba(255,31,0,0.18);
          color: #e0260a;
          font-size: 0.67rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 4px 14px;
          border-radius: 40px;
          margin-bottom: 16px;
        }

        .mc-hiw-h2 {
          font-size: clamp(1.9rem, 2.8vw, 2.6rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #0f1117;
          line-height: 1.12;
          margin-bottom: 14px;
        }

        .mc-hiw-p {
          font-size: 0.97rem;
          color: #6b7280;
          line-height: 1.72;
        }
        .mc-hiw-p strong { color: #1f2937; }

        /* ── step cards ── */
        .mc-steps-wrap {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 30px;
          margin-bottom: 28px;
        }

        .mc-step-card {
          background: #fafafa;
          border: 1.5px solid #f0f0f0;
          border-radius: 20px;
          padding: 32px 26px;
          position: relative;
          overflow: hidden;
          transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s;
        }
        .mc-step-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 24px 56px rgba(0,0,0,0.08);
          border-color: transparent;
        }

        /* step number - top right corner, huge faint */
        .mc-step-n {
            position: absolute;
            top: 10px;
            right: 26px;
            font-size: 6.5rem;
            font-weight: 900;
            line-height: 1;
            letter-spacing: -0.06em;
            pointer-events: none;
            user-select: none;
            opacity: 0.6;
        }
        .mc-step-card:nth-child(1) .mc-step-n { color: rgba(255,31,0,0.06); }
        .mc-step-card:nth-child(2) .mc-step-n { color: rgba(107,149,199,0.08); }
        .mc-step-card:nth-child(3) .mc-step-n { color: rgba(255,31,0,0.06); }

        /* left accent bar */
        .mc-step-card::before {
            content: '';
            position: absolute;
            top: 0px;
            left: 0px;
            width: 100%;
            border-radius: 0 3px 3px 0;
            height: 3px;
        }
        .mc-step-card:nth-child(1)::before { background: #ff1f00; }
        .mc-step-card:nth-child(2)::before { background: #6B95C7; }
        .mc-step-card:nth-child(3)::before {
          background: linear-gradient(90deg, #ff1f00, #6B95C7);
        }

        .mc-step-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px;
          flex-shrink: 0;
        }
        .mc-step-card:nth-child(1) .mc-step-icon { background: rgba(255,31,0,0.08); }
        .mc-step-card:nth-child(2) .mc-step-icon { background: rgba(107,149,199,0.12); }
        .mc-step-card:nth-child(3) .mc-step-icon { background: rgba(255,31,0,0.08); }

        .mc-step-tag {
          font-size: 0.59rem;
          font-weight: 700;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .mc-step-card:nth-child(1) .mc-step-tag { color: #e0260a; }
        .mc-step-card:nth-child(2) .mc-step-tag { color: #4a7fb5; }
        .mc-step-card:nth-child(3) .mc-step-tag { color: #e0260a; }

        .mc-step-title {
          font-size: 1rem;
          font-weight: 700;
          color: #0f1117;
          margin-bottom: 10px;
          line-height: 1.25;
        }

        .mc-step-desc {
          font-size: 0.83rem;
          color: #6b7280;
          line-height: 1.65;
        }

        /* ── connector arrows between cards ── */
        .mc-arrows {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
          margin-bottom: 64px;
        }

        .mc-arrow-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 0 8px;
        }

        .mc-arrow-cell:nth-child(1),
        .mc-arrow-cell:nth-child(3) {
          justify-content: flex-end;
        }

        .mc-arrow-line {
          flex: 1;
          height: 1.5px;
          background: linear-gradient(90deg, #ff1f00, #6B95C7);
          opacity: 0.2;
        }

        .mc-arrow-chevron {
          color: #9ca3af;
          font-size: 1.1rem;
          line-height: 1;
        }

        /* combined connector */
        .mc-step-connector {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          margin-bottom: 64px;
          padding: 0 20px;
        }

        .mc-conn-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .mc-conn-dot {
          width: 9px; height: 9px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .mc-conn-dot.r { background: #ff1f00; box-shadow: 0 0 0 4px rgba(255,31,0,0.1); }
        .mc-conn-dot.b { background: #6B95C7; box-shadow: 0 0 0 4px rgba(107,149,199,0.12); }
        .mc-conn-dot.g {
          background: linear-gradient(135deg, #ff1f00, #6B95C7);
          box-shadow: 0 0 0 4px rgba(107,149,199,0.1);
        }

        .mc-conn-lbl {
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #9ca3af;
          white-space: nowrap;
        }

        .mc-conn-line {
          flex: 1;
          height: 1.5px;
          margin: 0 4px;
          background: linear-gradient(90deg, #ff1f00, #6B95C7);
          opacity: 0.15;
          position: relative;
          top: -7px;
        }

        /* ── info cards (gift + whitelabel) ── */
        .mc-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 64px;
        }

        .mc-info-card {
          border-radius: 18px;
          padding: 30px 26px;
          position: relative;
          overflow: hidden;
        }

        .mc-info-card.ink {
          background: #0f1117;
          border: 1px solid rgba(255,255,255,0.06);
        }
        .mc-info-card.sky {
          background: #f0f6ff;
          border: 1px solid rgba(107,149,199,0.2);
        }

        .mc-info-eye {
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        .ink .mc-info-eye { color: #ff6b52; }
        .sky .mc-info-eye { color: #4a7fb5; }

        .mc-info-title {
          font-size: 1.1rem;
          font-weight: 800;
          line-height: 1.25;
          margin-bottom: 10px;
          letter-spacing: -0.02em;
        }
        .ink .mc-info-title { color: #ffffff; }
        .sky .mc-info-title { color: #0f1117; }

        .mc-info-desc {
          font-size: 0.84rem;
          line-height: 1.65;
        }
        .ink .mc-info-desc { color: rgba(255,255,255,0.42); }
        .sky .mc-info-desc { color: #6b7280; }

        .mc-info-icon {
          position: absolute;
          right: 16px; bottom: 12px;
          font-size: 4rem;
          opacity: 0.07;
          line-height: 1;
        }

        /* ── stats ── */
        .mc-num-head {
          text-align: center;
          margin-bottom: 32px;
        }
        .mc-num-eye {
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 6px;
        }
        .mc-num-title {
          font-size: 1.4rem;
          font-weight: 800;
          color: #0f1117;
          letter-spacing: -0.02em;
        }
        .mc-num-title em { font-style: normal; color: #ff1f00; }

        .mc-num-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        .mc-num-card {
          background: #fafafa;
          border: 1.5px solid #f0f0f0;
          border-radius: 16px;
          padding: 26px 18px;
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
        }
        .mc-num-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.07);
          border-color: transparent;
        }

        .mc-num-emoji { font-size: 1.6rem; margin-bottom: 10px; }
        .mc-num-val {
          font-size: 1.9rem;
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #0f1117;
          line-height: 1;
          margin-bottom: 6px;
        }
        .mc-num-val em { font-style: normal; color: #ff1f00; }
        .mc-num-lbl { font-size: 0.79rem; color: #9ca3af; line-height: 1.45; }

        /* ═══════════════════════════════════════
           FOOTER
        ═══════════════════════════════════════ */
        .mc-footer {
          background: #0f1117;
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 22px 0;
          text-align: center;
        }
        .mc-footer-copy {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.2);
          letter-spacing: 0.03em;
        }

        /* ═══════════════════════════════════════
           RESPONSIVE
        ═══════════════════════════════════════ */
        @media (max-width: 1100px) {
          .mc-hero-grid { grid-template-columns: 1fr 360px; gap: 48px; }
        }

        @media (max-width: 900px) {
          .mc-container { padding: 0 20px; }

          .mc-hero-grid {
            grid-template-columns: 1fr;
            gap: 40px;
            padding: 52px 0 64px;
            text-align: center;
          }
          .mc-hero-left { display: flex; flex-direction: column; align-items: center; }
          .mc-hero-sub { max-width: 100%; }
          .mc-stats-strip { justify-content: center; }
          .mc-card { max-width: 360px; margin: 0 auto; }

          .mc-steps-wrap { grid-template-columns: 1fr; gap: 16px; }
          .mc-step-connector { display: none; }
          .mc-info-grid { grid-template-columns: 1fr; }
          .mc-num-grid { grid-template-columns: 1fr; }
          .mc-nav-pill { display: none; }
        }

        @media (max-width: 600px) {
          .mc-container { padding: 0 16px; }
          .mc-hiw { padding: 64px 0; }
          .mc-stats-strip { flex-wrap: wrap; gap: 24px; }
        }
      `}</style>

      {/* ═══ NAVBAR ══════════════════════════════════════════ */}
      <nav className="mc-nav">
        <div className="mc-container">
          <div className="mc-nav-inner">
            <a href="#" className="mc-brand">
              <div className="mc-brand-logo">
                {AppLogo ? <img alt="Logo" src={AppLogo} /> : "🐋"}
              </div>
              <div>
                <div className="mc-brand-name">Moby Cashback</div>
                <div className="mc-brand-tagline">Marketing & Conversions App</div>
              </div>
            </a>
            <div className="mc-nav-pill">🇮🇳 India's First Shopper Exchange</div>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═════════════════════════════════════════════ */}
      <section className="mc-hero">
        <div className="mc-hero-bg" />

        <div className="mc-container">
          <div className="mc-hero-grid">

            {/* LEFT */}
            <div className="mc-hero-left">
              <div className="mc-eyebrow">
                <Smartphone size={12} /> Best experienced on mobile
              </div>

              <h1 className="mc-hero-title">
                Unlock the Full<br />
                Gifting Experience<br />
                on <span className="hi">Mobile</span>
              </h1>

              <p className="mc-hero-sub">
                Open Moby Cashback via QR scan or WhatsApp link. Claim gifts
                instantly, choose from 30,000+ premium offers, and enjoy
                seamless rewards on your phone.
              </p>

              <div className="mc-chips">
                <span className="mc-chip"><CheckCircle size={12} /> No App Needed</span>
                <span className="mc-chip"><CheckCircle size={12} /> 30K+ Offers</span>
                <span className="mc-chip"><CheckCircle size={12} /> 100% Free</span>
                <span className="mc-chip"><CheckCircle size={12} /> No Fraud Clicks</span>
              </div>

              <div className="mc-stats-strip">
                <div>
                  <div className="mc-snum">335<em>+</em></div>
                  <div className="mc-slbl">Partner Stores</div>
                </div>
                <div>
                  <div className="mc-snum">22<em> Mn+</em></div>
                  <div className="mc-slbl">Shoppers</div>
                </div>
                <div>
                  <div className="mc-snum">17<em>%</em></div>
                  <div className="mc-slbl">Conversion Rate</div>
                </div>
              </div>
            </div>

            {/* RIGHT - scanner card */}
            <div>
              <div className="mc-card">
                <div className="mc-card-label">📱 Scan or send to your phone</div>

                <div className="mc-qr-wrap">
                  <QRCodeGenerator value={referralUrl} size={186} />
                </div>

                <div className="mc-qr-hint">📷 Point your camera at the QR above</div>

                <div className="mc-or">or</div>

                <button className="mc-wa-btn" onClick={() => sendReferral("whatsapp")}>
                  <MessageCircle size={17} />
                  Send Link on WhatsApp
                </button>

                {statusMessage && (
                  <div className={`mc-status ${
                    statusMessage.toLowerCase().includes("success") ? "success"
                    : statusMessage.toLowerCase().includes("fail") || statusMessage.toLowerCase().includes("error") ? "error"
                    : "info"
                  }`}>
                    {statusMessage}
                  </div>
                )}

                <p className="mc-card-note">
                  <strong>No app needed.</strong> Opens in your mobile browser.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ════════════════════════════════════ */}
      <section className="mc-hiw">
        <div className="mc-container">

          <div className="mc-hiw-head">
            <div className="mc-pill">The Process</div>
            <h2 className="mc-hiw-h2">How Does This Work?</h2>
            <p className="mc-hiw-p">
              Use the power of Leaving Shoppers -{" "}
              <strong>only after they purchase</strong> - and get real new
              customers in exchange. No fraud clicks, no bots, 100% performance.
            </p>
          </div>

          {/* flow connector */}
          <div className="mc-step-connector">
            <div className="mc-conn-node">
              <div className="mc-conn-dot r" />
              <div className="mc-conn-lbl">Your Store</div>
            </div>
            <div className="mc-conn-line" />
            <div className="mc-conn-node">
              <div className="mc-conn-dot b" />
              <div className="mc-conn-lbl">Reward Store</div>
            </div>
            <div className="mc-conn-line" />
            <div className="mc-conn-node">
              <div className="mc-conn-dot g" />
              <div className="mc-conn-lbl">New Customers</div>
            </div>
          </div>
          
          {/* 3 step cards */}
          <div className="mc-steps-wrap">

            <div className="mc-step-card">
              <div className="mc-step-n">1</div>
              <div className="mc-step-icon">
                <ShoppingCart size={24} color="#ff1f00" strokeWidth={2} />
              </div>
              <div className="mc-step-tag">Your Website</div>
              <div className="mc-step-title">Customer Completes Purchase</div>
              <p className="mc-step-desc">
                Your customer reaches the "Thank You" page after checkout. Our
                snippet fires a "Claim Your Gift" button - only after a real,
                completed purchase.
              </p>
            </div>

            <div className="mc-step-card">
              <div className="mc-step-n">2</div>
              <div className="mc-step-icon">
                <Gift size={24} color="#6B95C7" strokeWidth={2} />
              </div>
              <div className="mc-step-tag">The Reward Store</div>
              <div className="mc-step-title">Shopper Claims Their Gift</div>
              <p className="mc-step-desc">
                Your shopper picks from non-competing deals and gift vouchers
                on the Moby Reward Store - any brand, online or offline, pan
                India. 30,000+ offers to choose from.
              </p>
            </div>

            <div className="mc-step-card">
              <div className="mc-step-n">3</div>
              <div className="mc-step-icon">
                <Users size={24} color="#ff1f00" strokeWidth={2} />
              </div>
              <div className="mc-step-tag">Our Partner Audience</div>
              <div className="mc-step-title">You Get Real New Customers</div>
              <p className="mc-step-desc">
                Verified shoppers from partner "Thank You" pages are pushed to
                your store with exclusive deals and our cashback. Real people.
                Real purchase intent.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* ═══ FOOTER ══════════════════════════════════════════ */}
      <footer className="mc-footer">
        <div className="mc-container">
          <div className="mc-footer-copy">
            © 2025 Moby Cashback · India's First Shopper Exchange
          </div>
        </div>
      </footer>
    </>
  );
};

export default MobileRedirectPage;